import {
  SignedIn,
  SignedOut,
  UserProfile,
  useOrganization,
  useUser,
} from "@clerk/clerk-react";
import {
  Button,
  Heading,
  LogoBlueOnWhite,
  PrimaryFullScreenSpinner,
  Txt,
} from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { RiShieldUserLine } from "react-icons/ri";
import logoBlueOnWhite from "~/assets/brand/logoBlueOnWhite.svg";
import { RedirectToSignInWithDelay } from "~/components/RedirectToSignInWithDelay";
import { opsHead } from "~/head";

export const Route = createFileRoute("/user-profile/")({
  head: () => opsHead("Profile"),
  component: UserProfileRoute,
});

export function UserProfileRoute() {
  const user = useUser();
  const { organization } = useOrganization();

  if (!user.isLoaded) {
    return <PrimaryFullScreenSpinner />;
  }

  const mfaRequired =
    !!organization?.publicMetadata?.["user_mfa_required"] &&
    user.user?.twoFactorEnabled !== true;

  return (
    <>
      <SignedIn>
        <Suspense fallback={<PrimaryFullScreenSpinner />}>
          <div className="grid min-h-dvh grid-rows-[5rem_1fr_5rem] gap-4 bg-gradient-to-t bg-zinc-50 from-zinc-200 to-zinc-50 ">
            <div className="place-self-start justify-self-start px-6 py-4">
              <LogoBlueOnWhite src={logoBlueOnWhite} />
            </div>
            <div className="grid place-content-center place-items-center gap-8">
              {mfaRequired && (
                <div className="mx-4 grid max-w-3xl justify-center justify-items-center gap-6 rounded-xl border border-warning-300 bg-warning-50 p-4 py-6 md:px-12">
                  <div className="grid gap-3">
                    <div className="grid gap-1">
                      <Heading
                        as="h1"
                        size="3xl"
                        align="center"
                        className="inline text-warning-900"
                      >
                        <RiShieldUserLine className="me-1 inline align-middle text-[1.3em]" />
                        <span className="align-middle">
                          Action required: enable two-step verification
                        </span>
                      </Heading>
                    </div>

                    <Txt align="center">
                      Please click{" "}
                      <Txt
                        size="sm"
                        className="whitespace-nowrap rounded-md border border-primary bg-primary px-1 py-0.5 "
                      >
                        + Add two-step verification
                      </Txt>{" "}
                      in the Security panel below, and follow the prompts.
                    </Txt>
                  </div>
                  <div className="grid">
                    <Txt align="center" size="sm" color="secondary">
                      Primary needs your help to keep your information secure.
                      This extra step helps prevent unauthorized access, even if
                      someone knows your password. <br />
                    </Txt>
                    <Button
                      variant="link"
                      size="xs-link"
                      className="justify-self-end"
                      asChild
                    >
                      <a href="https://clerk.chat/blog/how-2fa-enhances-security/">
                        What is two-step verification?
                      </a>
                    </Button>
                  </div>
                </div>
              )}
              <div className="min-h-[700px]">
                <UserProfile />
              </div>
            </div>
          </div>
        </Suspense>
      </SignedIn>
      <SignedOut>
        <RedirectToSignInWithDelay />
      </SignedOut>
    </>
  );
}
